import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLanguage } from './LanguageContext';

// Multilingual text interface
export interface MultilingualText {
  id: string;
  en: string;
}

// Original interfaces for content data structures
export interface SlideItem {
  id: number;
  title: MultilingualText;
  subtitle: MultilingualText;
  image: string;
  order: number;
  isActive: boolean;
}

export interface ProfileAdvantage {
  id: number;
  icon: string;
  title: string;
  description: string;
}

export interface ProfileContent {
  title: string;
  description: string;
  image: string;
  whyInvestTitle: string;
  whyInvestSubtitle: string;
  advantages: ProfileAdvantage[];
}

export interface Facility {
  id: number;
  name: string;
  description: string;
  icon: string;
  category: string;
  isActive: boolean;
}

export interface InvestmentOpportunity {
  id: number;
  title: string;
  description: string;
  image: string;
  sector: string;
  investmentRange: string;
  roi: string;
  location: string;
  isActive: boolean;
}

export interface FooterContent {
  description: string;
  contactInfo: {
    office: string;
    address: string;
    phone: string;
    email: string;
    officeHours: string;
  };
  copyright: string;
}

export interface WebsiteSettings {
  logo: string;
  adminLogo?: string;
  siteName: string;
  tagline: string;
  primaryColor: string;
  secondaryColor: string;
}

export interface ContactInfo {
  id: number;
  office: MultilingualText;
  description: MultilingualText;
  address: string;
  phone: string;
  email: string;
  officeHours: MultilingualText;
}

export interface ContactContent {
  pageTitle: MultilingualText;
  pageSubtitle: MultilingualText;
  infoTitle: MultilingualText;
  infoDescription: MultilingualText;
  formTitle: MultilingualText;
  formDescription: MultilingualText;
  formLabels: {
    fullName: MultilingualText;
    email: MultilingualText;
    phone: MultilingualText;
    company: MultilingualText;
    position: MultilingualText;
    message: MultilingualText;
    consent: MultilingualText;
    submit: MultilingualText;
    submitting: MultilingualText;
  };
  successMessage: {
    title: MultilingualText;
    message: MultilingualText;
  };
  errorMessage: {
    title: MultilingualText;
    message: MultilingualText;
  };
  contactInfo: ContactInfo;
}

// Content context interface
interface ContentContextType {
  // Data
  slides: SlideItem[];
  profileContent: ProfileContent;
  facilities: Facility[];
  investmentOpportunities: InvestmentOpportunity[];
  footerContent: FooterContent;
  websiteSettings: WebsiteSettings;
  contactContent: ContactContent;
  
  // Loading states
  isLoading: boolean;
  
  // CRUD operations for slides
  addSlide: (slide: Omit<SlideItem, 'id'>) => Promise<SlideItem>;
  updateSlide: (id: number, updates: Partial<SlideItem>) => Promise<void>;
  deleteSlide: (id: number) => Promise<void>;
  toggleSlide: (id: number) => Promise<void>;
  
  // CRUD operations for profile content
  updateProfileContent: (updates: Partial<ProfileContent>) => Promise<void>;
  addAdvantage: (advantage: Omit<ProfileAdvantage, 'id'>) => Promise<ProfileAdvantage>;
  updateAdvantage: (id: number, updates: Partial<ProfileAdvantage>) => Promise<void>;
  deleteAdvantage: (id: number) => Promise<void>;
  
  // CRUD operations for facilities
  addFacility: (facility: Omit<Facility, 'id'>) => Promise<Facility>;
  updateFacility: (id: number, updates: Partial<Facility>) => Promise<void>;
  deleteFacility: (id: number) => Promise<void>;
  toggleFacility: (id: number) => Promise<void>;
  
  // CRUD operations for investment opportunities
  addOpportunity: (opportunity: Omit<InvestmentOpportunity, 'id'>) => Promise<InvestmentOpportunity>;
  updateOpportunity: (id: number, updates: Partial<InvestmentOpportunity>) => Promise<void>;
  deleteOpportunity: (id: number) => Promise<void>;
  toggleOpportunity: (id: number) => Promise<void>;
  
  // Other content updates
  updateFooterContent: (updates: Partial<FooterContent>) => Promise<void>;
  updateWebsiteSettings: (updates: Partial<WebsiteSettings>) => Promise<void>;
  updateContactContent: (updates: Partial<ContactContent>) => Promise<void>;
  
  // Data refresh
  refreshContent: () => Promise<void>;
  
  // Helper function to get localized text
  getText: (text: MultilingualText) => string;
}

// Create context
const ContentContext = createContext<ContentContextType | undefined>(undefined);

// Custom hook to use content context
export const useContent = () => {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

// Initial default data
const getDefaultData = () => ({
  slides: [
    {
      id: 1,
      title: {
        id: 'Ruang Investasi Sukoharjo',
        en: 'Sukoharjo Investment Space'
      },
      subtitle: {
        id: 'Gerbang Menuju Investasi yang Makmur dan Berkelanjutan',
        en: 'Gateway to Prosperous and Sustainable Investment'
      },
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixid=M3w0NjE4NjN8MHwxfHNlYXJjaHwxfHxidWlsZGluZ3xlbnwwfHx8fDE2ODAwNzc2MjZ8MA&ixlib=rb-4.0.3&w=1920&h=1080&fit=crop&crop=center',
      order: 1,
      isActive: true
    },
    {
      id: 2,
      title: {
        id: 'Lokasi Strategis',
        en: 'Strategic Location'
      },
      subtitle: {
        id: 'Terhubung dengan Kota-kota Besar dan Jaringan Transportasi',
        en: 'Connected to Major Cities and Transportation Networks'
      },
      image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixid=M3w0NjE4NjN8MHwxfHNlYXJjaHwxfHxjaXR5fGVufDB8fHx8MTY4MDA3NzYyNnww&ixlib=rb-4.0.3&w=1920&h=1080&fit=crop&crop=center',
      order: 2,
      isActive: true
    },
    {
      id: 3,
      title: {
        id: 'Ekonomi Berkembang',
        en: 'Growing Economy'
      },
      subtitle: {
        id: 'Sektor Industri dan Pertanian yang Berkembang Pesat',
        en: 'Rapidly Growing Industrial and Agricultural Sectors'
      },
      image: 'https://images.unsplash.com/photo-1554469384-e58fac16e23a?ixid=M3w0NjE4NjN8MHwxfHNlYXJjaHwxfHxpbmR1c3RyeXxlbnwwfHx8fDE2ODAwNzc2MjZ8MA&ixlib=rb-4.0.3&w=1920&h=1080&fit=crop&crop=center',
      order: 3,
      isActive: true
    }
  ] as SlideItem[],
  
  profileContent: {
    title: 'Profil Kabupaten Sukoharjo',
    description: 'Kabupaten Sukoharjo adalah salah satu kabupaten di Provinsi Jawa Tengah yang terletak di bagian selatan Solo Raya. Dengan posisi strategis yang menghubungkan berbagai kota besar, Sukoharjo menjadi destinasi investasi yang menarik.',
    image: 'https://images.unsplash.com/photo-1473163928189-364b2c4e1135?ixlib=rb-4.0.3&w=600&h=400&fit=crop&crop=center',
    whyInvestTitle: 'Mengapa Berinvestasi di Sukoharjo?',
    whyInvestSubtitle: 'Temukan keunggulan dan peluang investasi yang menarik di Kabupaten Sukoharjo',
    advantages: [
      {
        id: 1,
        icon: 'MapPin',
        title: 'Lokasi Strategis',
        description: 'Terletak di jantung Jawa Tengah dengan akses mudah ke berbagai kota besar'
      },
      {
        id: 2,
        icon: 'TrendingUp',
        title: 'Ekonomi Berkembang',
        description: 'Pertumbuhan ekonomi yang konsisten dengan berbagai sektor unggulan'
      },
      {
        id: 3,
        icon: 'Users',
        title: 'SDM Berkualitas',
        description: 'Sumber daya manusia yang terampil dan siap mendukung investasi Anda'
      },
      {
        id: 4,
        icon: 'Zap',
        title: 'Infrastruktur Modern',
        description: 'Infrastruktur transportasi dan utilitas yang terus berkembang'
      }
    ]
  } as ProfileContent,
  
  facilities: [
    {
      id: 1,
      name: 'Jalan Tol Solo-Sragen',
      description: 'Akses jalan tol yang menghubungkan dengan berbagai kota',
      icon: 'Car',
      category: 'Transportasi',
      isActive: true
    },
    {
      id: 2,
      name: 'Bandara Internasional Adi Soemarmo',
      description: 'Bandara internasional dengan berbagai rute domestik dan internasional',
      icon: 'MapPin',
      category: 'Transportasi',
      isActive: true
    },
    {
      id: 3,
      name: 'Stasiun Kereta Api',
      description: 'Akses kereta api yang menghubungkan berbagai kota',
      icon: 'Car',
      category: 'Transportasi',
      isActive: true
    },
    {
      id: 4,
      name: 'Kawasan Industri Sukoharjo',
      description: 'Kawasan industri modern dengan fasilitas lengkap',
      icon: 'Building',
      category: 'Industri',
      isActive: true
    },
    {
      id: 5,
      name: 'Fasilitas Pendidikan',
      description: 'Sekolah dan universitas berkualitas untuk pengembangan SDM',
      icon: 'BookOpen',
      category: 'Pendidikan',
      isActive: true
    },
    {
      id: 6,
      name: 'Fasilitas Kesehatan',
      description: 'Rumah sakit dan klinik dengan standar pelayanan tinggi',
      icon: 'Heart',
      category: 'Kesehatan',
      isActive: true
    },
    {
      id: 7,
      name: 'Infrastruktur Energi',
      description: 'Suplai listrik dan energi yang stabil dan memadai',
      icon: 'Settings',
      category: 'Energi',
      isActive: true
    },
    {
      id: 8,
      name: 'Jaringan Telekomunikasi',
      description: 'Konektivitas internet dan telekomunikasi yang handal',
      icon: 'Wifi',
      category: 'Telekomunikasi',
      isActive: true
    },
    {
      id: 9,
      name: 'Sumber Daya Manusia',
      description: 'Tenaga kerja terampil dan siap pakai',
      icon: 'Users',
      category: 'SDM',
      isActive: true
    }
  ] as Facility[],
  
  investmentOpportunities: [
    {
      id: 1,
      title: 'Industri Gitar Baki',
      description: 'Pusat manufaktur gitar terkenal dunia dengan potensi ekspor yang besar',
      image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?ixlib=rb-4.0.3&w=600&h=400&fit=crop&crop=center',
      sector: 'Manufaktur',
      investmentRange: 'Rp 5-50 Miliar',
      roi: '15-25% per tahun',
      location: 'Kecamatan Baki',
      isActive: true
    },
    {
      id: 2,
      title: 'Industri Tekstil',
      description: 'Sektor manufaktur tekstil dan garmen yang berkembang pesat',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&w=600&h=400&fit=crop&crop=center',
      sector: 'Tekstil',
      investmentRange: 'Rp 1-20 Miliar',
      roi: '12-20% per tahun',
      location: 'Kawasan Industri',
      isActive: true
    },
    {
      id: 3,
      title: 'Agroindustri',
      description: 'Pengolahan hasil pertanian dan perkebunan',
      image: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?ixlib=rb-4.0.3&w=600&h=400&fit=crop&crop=center',
      sector: 'Pertanian',
      investmentRange: 'Rp 2-30 Miliar',
      roi: '10-18% per tahun',
      location: 'Zona Pertanian',
      isActive: true
    },
    {
      id: 4,
      title: 'Industri Furniture',
      description: 'Manufaktur mebel dan furniture berkualitas ekspor',
      image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?ixlib=rb-4.0.3&w=600&h=400&fit=crop&crop=center',
      sector: 'Manufaktur',
      investmentRange: 'Rp 3-25 Miliar',
      roi: '12-22% per tahun',
      location: 'Kawasan Industri',
      isActive: true
    },
    {
      id: 5,
      title: 'Industri Otomotif',
      description: 'Komponen dan suku cadang otomotif',
      image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-4.0.3&w=600&h=400&fit=crop&crop=center',
      sector: 'Otomotif',
      investmentRange: 'Rp 10-100 Miliar',
      roi: '18-30% per tahun',
      location: 'Kawasan Industri',
      isActive: true
    },
    {
      id: 6,
      title: 'Pariwisata',
      description: 'Pengembangan destinasi wisata dan hospitality',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&w=600&h=400&fit=crop&crop=center',
      sector: 'Pariwisata',
      investmentRange: 'Rp 5-75 Miliar',
      roi: '10-25% per tahun',
      location: 'Area Wisata',
      isActive: true
    }
  ] as InvestmentOpportunity[],
  
  footerContent: {
    description: 'Ruang Investasi Sukoharjo - Gerbang Anda menuju peluang investasi yang makmur dan berkelanjutan.',
    contactInfo: {
      office: 'DPMPTSP Sukoharjo',
      address: 'Jl. Jend. Sudirman No. 1, Sukoharjo, Jawa Tengah 57512, Indonesia',
      phone: '+62 271 593156',
      email: 'dpmptsp@sukoharjokab.go.id',
      officeHours: 'Senin - Jumat: 08:00 - 16:00 WIB\\nSabtu: 08:00 - 12:00 WIB\\nMinggu: Tutup'
    },
    copyright: '© 2024 DPMPTSP Kabupaten Sukoharjo. Semua hak dilindungi.'
  } as FooterContent,
  
  websiteSettings: {
    logo: '/logo192.png',
    siteName: 'Ruang Investasi Sukoharjo',
    tagline: 'Investment Gateway to Prosperity',
    primaryColor: '#1e3a8a',
    secondaryColor: '#059669'
  } as WebsiteSettings,
  
  contactContent: {
    pageTitle: { id: 'Hubungi Kami', en: 'Contact Us' },
    pageSubtitle: { id: 'Siap berinvestasi di Sukoharjo? Hubungi tim promosi investasi kami untuk membahas peluang Anda.', en: 'Ready to invest in Sukoharjo? Get in touch with our investment team to discuss your opportunities.' },
    infoTitle: { id: 'Hubungi Kami', en: 'Get In Touch' },
    infoDescription: { id: 'Tim promosi investasi kami siap membantu Anda dengan rencana investasi di Sukoharjo. Hubungi kami melalui saluran berikut:', en: 'Our investment promotion team is ready to assist you with your investment plans in Sukoharjo. Contact us through any of the following channels:' },
    formTitle: { id: 'Formulir Pertanyaan Investasi', en: 'Investment Inquiry Form' },
    formDescription: { id: 'Isi formulir ini dan tim kami akan menghubungi Anda dalam 24 jam.', en: 'Fill out this form and our team will get back to you within 24 hours.' },
    formLabels: {
      fullName: { id: 'Nama Lengkap', en: 'Full Name' },
      email: { id: 'Alamat Email', en: 'Email Address' },
      phone: { id: 'Nomor Telepon', en: 'Phone Number' },
      company: { id: 'Nama Perusahaan', en: 'Company Name' },
      position: { id: 'Posisi/Jabatan', en: 'Position/Title' },
      message: { id: 'Pesan', en: 'Message' },
      consent: { id: 'Saya setuju dengan pemrosesan data pribadi saya untuk tujuan promosi investasi dan menyetujui untuk dihubungi oleh DPMPTSP Sukoharjo terkait pertanyaan investasi saya.', en: 'I agree to the processing of my personal data for investment promotion purposes and consent to be contacted by DPMPTSP Sukoharjo regarding my investment inquiry.' },
      submit: { id: 'Kirim Pertanyaan', en: 'Send Inquiry' },
      submitting: { id: 'Mengirim...', en: 'Submitting...' }
    },
    successMessage: {
      title: { id: 'Terima Kasih!', en: 'Thank You!' },
      message: { id: 'Pertanyaan Anda telah berhasil dikirim. Tim kami akan menghubungi Anda dalam 24 jam.', en: 'Your inquiry has been submitted successfully. Our team will contact you within 24 hours.' }
    },
    errorMessage: {
      title: { id: 'Error', en: 'Error' },
      message: { id: 'Terjadi kesalahan saat mengirim formulir. Silakan coba lagi nanti.', en: 'There was an error submitting your form. Please try again later.' }
    },
    contactInfo: {
      id: 1,
      office: { id: 'DPMPTSP Sukoharjo', en: 'DPMPTSP Sukoharjo' },
      description: { id: 'Dinas Penanaman Modal dan Pelayanan Terpadu Satu Pintu Kabupaten Sukoharjo', en: 'Investment and Integrated Services Office of Sukoharjo Regency' },
      address: 'Jl. Jend. Sudirman No. 1, Sukoharjo, Central Java 57512, Indonesia',
      phone: '+62 271 593156',
      email: 'dpmptsp@sukoharjokab.go.id',
      officeHours: { id: 'Senin - Jumat: 08:00 - 16:00 WIB\nSabtu: 08:00 - 12:00 WIB\nMinggu: Tutup', en: 'Monday - Friday: 08:00 - 16:00 WIB\nSaturday: 08:00 - 12:00 WIB\nSunday: Closed' }
    }
  } as ContactContent
});

// Provider component with all the same functionality
export const ContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentLanguage } = useLanguage();
  const [data, setData] = useState(getDefaultData());
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to migrate old slide data to multilingual format
  const migrateSlideData = (slides: any[]): SlideItem[] => {
    // Translation mapping for common slide texts
    const titleTranslations: { [key: string]: string } = {
      'Ruang Investasi Sukoharjo': 'Sukoharjo Investment Space',
      'Lokasi Strategis': 'Strategic Location', 
      'Ekonomi Berkembang': 'Growing Economy',
      'Slide Baru': 'New Slide'
    };
    
    const subtitleTranslations: { [key: string]: string } = {
      'Gerbang Menuju Investasi yang Makmur dan Berkelanjutan': 'Gateway to Prosperous and Sustainable Investment',
      'Terhubung dengan Kota-kota Besar dan Jaringan Transportasi': 'Connected to Major Cities and Transportation Networks',
      'Sektor Industri dan Pertanian yang Berkembang Pesat': 'Rapidly Growing Industrial and Agricultural Sectors',
      'Deskripsi slide baru': 'New slide description'
    };
    
    return slides.map((slide: any) => ({
      ...slide,
      title: typeof slide.title === 'string' 
        ? { 
            id: slide.title, 
            en: titleTranslations[slide.title] || slide.title 
          }
        : slide.title, // Already multilingual
      subtitle: typeof slide.subtitle === 'string' 
        ? { 
            id: slide.subtitle, 
            en: subtitleTranslations[slide.subtitle] || slide.subtitle 
          }
        : slide.subtitle // Already multilingual
    }));
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        const savedData = localStorage.getItem('sukoharjo-content-data');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const defaultData = getDefaultData();
          
          console.log('🔄 Loading saved data:', parsedData);
          
          // Check if migration is needed - only migrate if version is not 2.0 or slides contain strings
          const needsMigration = parsedData.slides && Array.isArray(parsedData.slides) && 
            (parsedData.version !== '2.0' || 
             parsedData.slides.some((slide: any) => typeof slide.title === 'string' || typeof slide.subtitle === 'string'));
            
          if (needsMigration) {
            console.log('🔄 🔄 MIGRATION NEEDED - Converting slide data to multilingual format');
            parsedData.slides = migrateSlideData(parsedData.slides);
            parsedData.version = '2.0'; // Mark as migrated
            console.log('🔄 ✅ Migrated slides data:', parsedData.slides);
            console.log('🔄 📋 First slide after migration:', parsedData.slides[0]);
            
            // Save migrated data back to localStorage
            localStorage.setItem('sukoharjo-content-data', JSON.stringify(parsedData));
          } else if (parsedData.slides) {
            console.log('🔄 ✅ Slides data already in multilingual format, version:', parsedData.version);
          }
          
          // Use saved data with careful merging to preserve user changes
          const mergedData = {
            ...defaultData,
            ...parsedData,
            // Ensure slides from saved data take priority
            slides: parsedData.slides || defaultData.slides
          };
          
          setData(mergedData);
          console.log('🔄 Final merged data set to context:');
          console.log('🔄 Slides count:', mergedData.slides?.length);
          console.log('🔄 First slide image:', mergedData.slides?.[0]?.image);
          console.log('🔄 Parsed data slides:', parsedData.slides?.length);
          console.log('🔄 Default data slides:', defaultData.slides?.length);
        } else {
          console.log('🔄 No saved data found, using default multilingual data');
          const defaultData = getDefaultData();
          const dataWithVersion = {
            ...defaultData,
            version: '2.0'
          };
          setData(dataWithVersion);
          localStorage.setItem('sukoharjo-content-data', JSON.stringify(dataWithVersion));
        }
      } catch (error) {
        console.error('Error loading content data:', error);
        // Fallback to default data if parsing fails
        const defaultData = getDefaultData();
        const dataWithVersion = {
          ...defaultData,
          version: '2.0'
        };
        setData(dataWithVersion);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
    
    // Add debug functions to window for troubleshooting
    (window as any).resetSliderData = () => {
      localStorage.removeItem('sukoharjo-content-data');
      console.log('💫 Manual reset performed - page will reload');
      window.location.reload();
    };
    
    (window as any).forceSliderMigration = () => {
      const data = JSON.parse(localStorage.getItem('sukoharjo-content-data') || '{}');
      delete data.version; // Remove version to force migration
      localStorage.setItem('sukoharjo-content-data', JSON.stringify(data));
      console.log('💫 Force migration triggered - page will reload');
      window.location.reload();
    };
    
    (window as any).viewSliderData = () => {
      const data = localStorage.getItem('sukoharjo-content-data');
      if (data) {
        const parsed = JSON.parse(data);
        console.log('🔍 Current slider data in localStorage:', parsed.slides);
        console.log('🔍 Version:', parsed.version);
        return parsed;
      } else {
        console.log('🔍 No data in localStorage');
        return null;
      }
    };
  }, []);

  // Helper function to save data
  const saveData = async (newData: typeof data) => {
    console.log('💾 Context saveData called with:', newData.slides?.length, 'slides');
    setIsLoading(true);
    try {
      localStorage.setItem('sukoharjo-content-data', JSON.stringify(newData));
      console.log('💾 Data successfully saved to localStorage with key: sukoharjo-content-data');
      setData(newData);
      console.log('💾 Context state updated with new data');
    } catch (error) {
      console.error('🚫 Error saving content data:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // All the CRUD operations remain the same...
  const addSlide = async (slideData: Omit<SlideItem, 'id'>): Promise<SlideItem> => {
    console.log('🟡 Context addSlide called with data:', slideData);
    const newSlide: SlideItem = { ...slideData, id: Date.now() };
    console.log('🟡 Created new slide:', newSlide);
    const newData = { ...data, slides: [...data.slides, newSlide] };
    console.log('🟡 New slides array:', newData.slides);
    await saveData(newData);
    console.log('🟡 addSlide completed, data saved to localStorage');
    return newSlide;
  };

  const updateSlide = async (id: number, updates: Partial<SlideItem>): Promise<void> => {
    console.log('🔴 Context updateSlide called - ID:', id, 'Updates:', updates);
    console.log('🔴 Current slides in context:', data.slides);
    const newData = {
      ...data,
      slides: data.slides.map(slide => slide.id === id ? { ...slide, ...updates } : slide)
    };
    console.log('🔴 New slides data:', newData.slides);
    await saveData(newData);
    console.log('🔴 updateSlide completed, data saved to localStorage');
  };

  const deleteSlide = async (id: number): Promise<void> => {
    console.log('📛 Context deleteSlide called - ID:', id);
    console.log('📛 Current slides before delete:', data.slides);
    const newData = { ...data, slides: data.slides.filter(slide => slide.id !== id) };
    console.log('📛 New slides after delete:', newData.slides);
    await saveData(newData);
    console.log('📛 deleteSlide completed, data saved to localStorage');
  };

  const toggleSlide = async (id: number): Promise<void> => {
    await updateSlide(id, { isActive: !data.slides.find(s => s.id === id)?.isActive });
  };

  const updateProfileContent = async (updates: Partial<ProfileContent>): Promise<void> => {
    const newData = { ...data, profileContent: { ...data.profileContent, ...updates } };
    await saveData(newData);
  };

  const addAdvantage = async (advantageData: Omit<ProfileAdvantage, 'id'>): Promise<ProfileAdvantage> => {
    const newAdvantage: ProfileAdvantage = { ...advantageData, id: Date.now() };
    const newData = {
      ...data,
      profileContent: {
        ...data.profileContent,
        advantages: [...data.profileContent.advantages, newAdvantage]
      }
    };
    await saveData(newData);
    return newAdvantage;
  };

  const updateAdvantage = async (id: number, updates: Partial<ProfileAdvantage>): Promise<void> => {
    const newData = {
      ...data,
      profileContent: {
        ...data.profileContent,
        advantages: data.profileContent.advantages.map(advantage => 
          advantage.id === id ? { ...advantage, ...updates } : advantage
        )
      }
    };
    await saveData(newData);
  };

  const deleteAdvantage = async (id: number): Promise<void> => {
    const newData = {
      ...data,
      profileContent: {
        ...data.profileContent,
        advantages: data.profileContent.advantages.filter(advantage => advantage.id !== id)
      }
    };
    await saveData(newData);
  };

  const addFacility = async (facilityData: Omit<Facility, 'id'>): Promise<Facility> => {
    const newFacility: Facility = { ...facilityData, id: Date.now() };
    const newData = { ...data, facilities: [...data.facilities, newFacility] };
    await saveData(newData);
    return newFacility;
  };

  const updateFacility = async (id: number, updates: Partial<Facility>): Promise<void> => {
    const newData = {
      ...data,
      facilities: data.facilities.map(facility => 
        facility.id === id ? { ...facility, ...updates } : facility
      )
    };
    await saveData(newData);
  };

  const deleteFacility = async (id: number): Promise<void> => {
    const newData = { ...data, facilities: data.facilities.filter(facility => facility.id !== id) };
    await saveData(newData);
  };

  const toggleFacility = async (id: number): Promise<void> => {
    await updateFacility(id, { isActive: !data.facilities.find(f => f.id === id)?.isActive });
  };

  const addOpportunity = async (opportunityData: Omit<InvestmentOpportunity, 'id'>): Promise<InvestmentOpportunity> => {
    const newOpportunity: InvestmentOpportunity = { ...opportunityData, id: Date.now() };
    const newData = { ...data, investmentOpportunities: [...data.investmentOpportunities, newOpportunity] };
    await saveData(newData);
    return newOpportunity;
  };

  const updateOpportunity = async (id: number, updates: Partial<InvestmentOpportunity>): Promise<void> => {
    const newData = {
      ...data,
      investmentOpportunities: data.investmentOpportunities.map(opportunity => 
        opportunity.id === id ? { ...opportunity, ...updates } : opportunity
      )
    };
    await saveData(newData);
  };

  const deleteOpportunity = async (id: number): Promise<void> => {
    const newData = {
      ...data,
      investmentOpportunities: data.investmentOpportunities.filter(opportunity => opportunity.id !== id)
    };
    await saveData(newData);
  };

  const toggleOpportunity = async (id: number): Promise<void> => {
    await updateOpportunity(id, { isActive: !data.investmentOpportunities.find(o => o.id === id)?.isActive });
  };

  const updateFooterContent = async (updates: Partial<FooterContent>): Promise<void> => {
    const newData = { ...data, footerContent: { ...data.footerContent, ...updates } };
    await saveData(newData);
  };

  const updateWebsiteSettings = async (updates: Partial<WebsiteSettings>): Promise<void> => {
    const newData = { ...data, websiteSettings: { ...data.websiteSettings, ...updates } };
    await saveData(newData);
  };

  const updateContactContent = async (updates: Partial<ContactContent>): Promise<void> => {
    const newData = { ...data, contactContent: { ...data.contactContent, ...updates } };
    await saveData(newData);
  };

  // Helper function to get localized text
  const getText = (text: MultilingualText): string => {
    return text[currentLanguage] || text.en;
  };

  const refreshContent = async (): Promise<void> => {
    console.log('🔄 Context refreshContent called');
    setIsLoading(true);
    try {
      const savedData = localStorage.getItem('sukoharjo-content-data');
      console.log('🔄 Data from localStorage:', savedData ? 'exists' : 'not found');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        console.log('🔄 Parsed data slides:', parsedData.slides?.length, 'slides');
        const defaultData = getDefaultData();
        
        // Use saved data completely, only fall back to default for missing properties
        const mergedData = {
          ...defaultData,
          ...parsedData
        };
        
        console.log('🔄 Setting merged data with', mergedData.slides?.length, 'slides');
        setData(mergedData);
      }
    } catch (error) {
      console.error('🚫 Error refreshing content data:', error);
    } finally {
      setIsLoading(false);
      console.log('🔄 refreshContent completed');
    }
  };

  const contextValue: ContentContextType = {
    slides: data.slides,
    profileContent: data.profileContent,
    facilities: data.facilities,
    investmentOpportunities: data.investmentOpportunities,
    footerContent: data.footerContent,
    websiteSettings: data.websiteSettings,
    contactContent: data.contactContent,
    isLoading,
    addSlide, updateSlide, deleteSlide, toggleSlide,
    updateProfileContent, addAdvantage, updateAdvantage, deleteAdvantage,
    addFacility, updateFacility, deleteFacility, toggleFacility,
    addOpportunity, updateOpportunity, deleteOpportunity, toggleOpportunity,
    updateFooterContent, updateWebsiteSettings, updateContactContent,
    refreshContent, getText
  };

  return (
    <ContentContext.Provider value={contextValue}>
      {children}
    </ContentContext.Provider>
  );
};